import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, Check, Calculator, Database, Scale, FileText } from 'lucide-react'
import { DARK, TEAL, BG, MarketingNavBar, MarketingFooter, FaqAccordion, faqJsonLd, KeywordCrossLinks } from '../../components/MarketingLayout'

const FAQS = [
  {
    q: 'Hogyan számolja ki a rendszer a recept kalóriatartalmát?',
    a: 'Minden alapanyaghoz tárolva van a 100 g-ra vetített tápértéke (kalória, fehérje, zsír, szénhidrát). A recept beírt mennyiségei alapján a rendszer összesíti ezeket, majd elosztja az adagok számával — így megkapod a teljes recept és az egy adagra jutó tápértéket is.',
  },
  {
    q: 'Milyen alapanyag-adatbázisból dolgozik a számítás?',
    a: '400+ beépített alapanyag tápértékadatait tartalmazza a rendszer, ezen felül egyedi alapanyagokat is felvehetsz saját tápértékadatokkal, ha valami hiányozna.',
  },
  {
    q: 'Csak kalóriát számol, vagy mást is?',
    a: 'Kalórián kívül fehérje, zsír (ezen belül telített zsír) és szénhidrát (ezen belül cukor) tartalmat is számol, az EU 1169/2011 rendeletnek megfelelő táblázat formájában, 100 g-ra és adagra vetítve is.',
  },
  {
    q: 'A kalóriaszámítás bekerül a gyártmánylapba is?',
    a: 'Igen. Ugyanaz a tápértékszámítás automatikusan megjelenik a gyártmánylapon, az etikett-címkén és a származási igazoláson is — nem kell külön kiszámolni.',
  },
  {
    q: 'Ingyenesen kipróbálható a kalkulátor?',
    a: 'Igen, regisztráció után 3 recept erejéig ingyenesen kipróbálhatod a receptből kalória számítást, kártyaszám megadása nélkül.',
  },
]

export default function KaloriaSzamitasPage() {
  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>Receptből kalória számítás automatikusan — CukrászDoc</title>
        <meta name="description" content="Add meg a recept alapanyagait és mennyiségeit — a rendszer automatikusan kiszámolja a kalóriát, fehérjét, zsírt és szénhidrátot, 100 g-ra és adagra is. 400+ alapanyag beépítve." />
        <link rel="canonical" href="https://cukraszdoc.hu/receptbol-kaloria-szamitas" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd(FAQS))}</script>
      </Helmet>
      <MarketingNavBar />

      <section style={{ background: `linear-gradient(160deg, ${DARK} 0%, #162119 100%)`, paddingTop: '5rem' }} className="relative overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-6 py-16 lg:py-24">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }}>
            <Calculator size={12} />
            400+ alapanyag beépítve
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight text-white mb-5">
            Receptből kalória számítás <span style={{ color: TEAL }}>automatikusan</span>
          </h1>
          <p className="text-lg sm:text-xl max-w-2xl leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Beírod az alapanyagokat és a mennyiségeket, a CukrászDoc kiszámolja a kalória-, fehérje-, zsír- és szénhidráttartalmat — 100 grammra és egy adagra vetítve is, kézi utánaszámolás nélkül.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-base transition-all" style={{ background: TEAL, color: '#fff' }}>
              Számold ki ingyen <ArrowRight size={16} />
            </Link>
          </div>
        </div>
        <div className="relative" style={{ height: 60, marginBottom: -2 }}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="absolute bottom-0 w-full h-full">
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-6" style={{ color: DARK }}>Hogyan működik a kalóriaszámítás?</h2>
          <p className="text-base leading-relaxed text-gray-600 mb-4">
            Minden alapanyaghoz rögzítve van a 100 grammra vetített tápértéke. Amikor beírod a recept összetevőit a felhasznált mennyiségekkel, a rendszer alapanyagonként kiszámolja a kalória-, fehérje-, zsír- és szénhidráttartalmat, majd összesíti az egész receptre.
          </p>
          <p className="text-base leading-relaxed text-gray-600">
            Az összesített értéket elosztja a megadott adagszámmal, így egyszerre látod a teljes tétel és az egy szelet/darab/adag tápértékét is — pontosan úgy, ahogy a kötelező tápértéktáblázathoz (EU 1169/2011) szükséges.
          </p>
        </div>
      </section>

      <section style={{ background: BG }} className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: DARK }}>Mit kapsz a kalóriaszámítással?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Database, title: '400+ alapanyag', desc: 'Beépített tápértékadatbázis, egyedi alapanyag is felvehető.' },
              { icon: Scale, title: '100 g és adag szerint', desc: 'A teljes recept és egy adag tápértéke is látszik.' },
              { icon: Calculator, title: 'Kalória, fehérje, zsír, szénhidrát', desc: 'Telített zsír és cukor bontásban is.' },
              { icon: FileText, title: 'Automatikusan a dokumentumokban', desc: 'Megjelenik a gyártmánylapon és az etikett-címkén is.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-6" style={{ border: '1px solid rgba(91,184,160,0.15)' }}>
                <Icon size={22} style={{ color: TEAL }} className="mb-3" />
                <h3 className="font-semibold mb-2 text-base" style={{ color: DARK }}>{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-10" style={{ color: DARK }}>Gyakori kérdések a kalóriaszámításról</h2>
          <FaqAccordion items={FAQS} />
        </div>
      </section>

      <KeywordCrossLinks current="/receptbol-kaloria-szamitas" />

      <section style={{ background: DARK }} className="py-20">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Próbáld ki — ingyenes</h2>
          <p className="mb-8 text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>Regisztrálj, írd be az első receptedet, és azonnal látod a kiszámolt tápértéket.</p>
          <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base" style={{ background: TEAL, color: '#fff' }}>
            <Check size={17} /> Ingyenes regisztráció
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
